import React, { useEffect, useState } from "react"
import { Row, Col, Card, CardBody, CardTitle, Badge, Table } from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { dashboardAPI } from "../../services/api"
import ReactApexChart from "react-apexcharts"
import { Link, useNavigate } from "react-router-dom"
import GanttChart from "../../components/GanttChart/GanttChart"

const TicketDashboard = (props) => {
    document.title = "Dashboard | Ticket Management"

    const [stats, setStats] = useState(null)
    const [dispo, setDispo] = useState([])
    const [avancement, setAvancement] = useState([])
    const [echeances, setEcheances] = useState({ projets: [], tickets: [] })

    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const isClient = user.role === 'client'
    const isCollaborateur = user.role === 'collaborateur'
    const navigate = useNavigate()

    useEffect(() => {
        if (isCollaborateur) {
            navigate("/tickets")
        }
    }, [isCollaborateur, navigate])

    useEffect(() => {
        props.setBreadcrumbItems("Tableau de bord", [{ title: "Menu", link: "#" }, { title: "Dashboard", link: "#" }])
        fetchDashboardData()
    }, [])

    const fetchDashboardData = async () => {
        try {
            const [statsRes, dispoRes, avanRes, echRes] = await Promise.all([
                dashboardAPI.getStats(),
                dashboardAPI.getDisponibiliteEquipe(),
                dashboardAPI.getProjetsAvancement(),
                dashboardAPI.getEcheances()
            ])
            setStats(statsRes.data)
            setDispo(dispoRes.data)
            setAvancement(avanRes.data)
            setEcheances(echRes.data)
        } catch (error) {
            console.error("Erreur dashboard", error)
        }
    }

    const reports = stats ? [
        { title: "Projets", iconClass: "briefcase-check", total: stats.projets_count, variation: stats.projets_variation, badgecolor: "info" },
        { title: "Tickets Actifs", iconClass: "ticket-confirmation", total: stats.tickets_actifs, variation: stats.tickets_actifs_variation, badgecolor: "warning" },
        { title: "Tickets Résolus", iconClass: "check-circle-outline", total: stats.tickets_resolus, variation: stats.tickets_resolus_variation, badgecolor: "success" },
        ...(!isClient ? [{ title: "Équipe", iconClass: "account-group", total: stats.collaborateurs_count, variation: stats.collaborateurs_variation, badgecolor: "primary" }] : []),
    ] : []

    // Avancement Chart
    const avancementOptions = {
        chart: { type: 'bar', height: 350 },
        plotOptions: { bar: { horizontal: true } },
        xaxis: { categories: avancement.map(p => p.nom) },
        colors: ['#3c4ccf', '#02a499'],
    }
    const avancementSeries = [
        { name: 'Réalisé', data: avancement.map(p => p.avancement_realise) },
        { name: 'Prévu', data: avancement.map(p => p.avancement_prevu) }
    ]

    // Disponibilité Chart
    const dispoOptions = {
        chart: { type: 'donut', height: 300 },
        labels: ['Libre', 'Partiel', 'Occupé'],
        colors: ['#02a499', '#f8b425', '#ec4561'],
    }
    const counts = { libre: 0, partiel: 0, occupe: 0 }
    dispo.forEach(d => counts[d.disponibilite]++)
    const dispoSeries = [counts.libre, counts.partiel, counts.occupe]

    return (
        <React.Fragment>
            {/* Cards Widgets */}
            <Row>
                {reports.map((report, key) => (
                    <Col xl="3" md="6" key={key}>
                        <Card className="mini-stat bg-primary">
                            <CardBody className="mini-stat-img">
                                <div className="mini-stat-icon">
                                    <i className={`mdi mdi-${report.iconClass} float-end`}></i>
                                </div>
                                <div className="text-white">
                                    <h6 className="text-uppercase mb-3 font-size-16 text-white">{report.title}</h6>
                                    <h2 className="mb-4 text-white">{report.total}</h2>
                                    <Badge color={report.variation >= 0 ? "success" : "danger"} className="ms-1">
                                        {report.variation >= 0 ? "+" : ""}{report.variation}%
                                    </Badge>
                                    <span className="ms-2">Depuis le mois dernier</span>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row>
                <Col xl={isClient ? 12 : 8}>
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Avancement des Projets</CardTitle>
                            <ReactApexChart options={avancementOptions} series={avancementSeries} type="bar" height={350} />
                        </CardBody>
                    </Card>
                </Col>
                {!isClient && (
                    <Col xl="4">
                        <Card>
                            <CardBody>
                                <CardTitle className="h4 mb-4">Disponibilité de l'équipe</CardTitle>
                                <ReactApexChart options={dispoOptions} series={dispoSeries} type="donut" height={300} />
                                <div className="mt-4">
                                    {dispo.map(user => (
                                        <div key={user.id} className="d-flex justify-content-between mb-2">
                                            <span>{user.name}</span>
                                            <Badge color={user.disponibilite === 'occupe' ? 'danger' : (user.disponibilite === 'partiel' ? 'warning' : 'success')}>
                                                {user.tickets_en_cours} tickets
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Gantt Chart - Planning des Projets */}
            <Row>
                <Col xl="12">
                    <GanttChart />
                </Col>
            </Row>

            <Row>
                <Col xl="6">
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Échéances Proches (Sous 7 jours)</CardTitle>
                            <Table responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Nom</th>
                                        <th>Échéance</th>
                                        <th>Statut</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.projets.map(p => (
                                        <tr key={`p-${p.id}`}>
                                            <td><Badge color="info">PROJET</Badge></td>
                                            <td>{p.nom}</td>
                                            <td>{new Date(p.date_fin_prevue).toLocaleDateString()}</td>
                                            <td>{p.statut}</td>
                                        </tr>
                                    ))}
                                    {echeances.tickets.map(t => (
                                        <tr key={`t-${t.id}`}>
                                            <td><Badge color="warning">TICKET</Badge></td>
                                            <td>{t.titre}</td>
                                            <td>{new Date(t.deadline).toLocaleDateString()}</td>
                                            <td>{t.statut}</td>
                                        </tr>
                                    ))}
                                    {echeances.projets.length === 0 && echeances.tickets.length === 0 && (
                                        <tr><td colSpan="4" className="text-center">Aucune échéance proche</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
                <Col xl="6">
                    <Card>
                        <CardBody>
                            <CardTitle className="h4 mb-4">Tickets Récents</CardTitle>
                            <div className="table-responsive">
                                <Table className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Titre</th>
                                            <th>Projet</th>
                                            <th>Assigné à</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats?.recent_tickets.map(t => (
                                            <tr key={t.id}>
                                                <td><Link to={`/tickets/${t.id}`}>{t.titre}</Link></td>
                                                <td>{t.projet?.nom}</td>
                                                <td>{t.assigned_to?.name || '-'}</td>
                                                <td><Badge color="primary">{t.statut}</Badge></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(TicketDashboard)
